/**
 * micro:bit 놀이터 장면 별 3단계(2026-10-08 — 사용자 승인 기준표) — 스마트브릭 기구 무대처럼 ⭐ · ⭐⭐ · ⭐⭐⭐. 순수 규칙.
 *  - ⭐ = 지금 장면 목표 그대로(오늘의 미션 — Campus 미션 판정과 같다 · 부르는 쪽이 star1 로 넘긴다)
 *  - 횟수 장면: ⭐⭐ · ⭐⭐⭐ = 목표의 약 2배 · 3배(장면 상태가 목표 뒤에도 센다)
 *  - 끝이 있는 장면: ⭐⭐ = 장면을 연 뒤 2분 안에 · ⭐⭐⭐ = 1분 안에 해냄(진짜 보드로만 되는 장면은 3분 · 2분)
 *  - 균형 잡기: 가장 오래 버틴 시간 5초 · 10초 · 15초
 * 운영 콘솔 '무대 목표'(카탈로그 mb_goal)로 장면마다 별 둘 · 셋 기준 · 문구를 바꿀 수 있다 — 모양이 틀리면 기본 기준표를 쓴다.
 * 별은 나만의 별 — 친구와 견주지 않는다. 별 둘 · 셋은 별 하나를 해낸 뒤에만 켜진다.
 */
import type { SceneId } from "./scenes";
import type { Lessons } from "./lessons";
import type { PlayState } from "./play";
import type { Heartbeat, Movie, Track } from "./scenes";
import type { Plaza } from "./radio";
import { repeatsOf } from "./scroll";
import { BOARD_LEN, BRICKS_MAX, GOALS_MAX, fastStarts } from "./games";
import { FRIENDS_MAX, PAGES_MAX, SHIPS_MAX } from "./little";
import { TRACK_MAX } from "./scenes";
import { SHOWCASE_VISITORS_MAX } from "./stages";
import { BALANCE_MAX_MS } from "./remotes";

export interface StarsInput {
  readonly scene: SceneId;
  /** 지금 장면 목표를 해냈나(lib/sceneDone — 오늘의 미션) */
  readonly star1: boolean;
  readonly lessons: Lessons;
  readonly play: PlayState;
  readonly track: Track;
  readonly heart: Heartbeat;
  readonly movie: Movie;
  readonly plaza: Plaza;
  /** 장면을 연 뒤 처음 해낸 때까지(ms · 아직이거나 열 때 이미 해냈으면 null) */
  readonly doneMs: number | null;
}

/** 목표 줄 하나 — 문구 열쇠 · 값(i18n) · 해냄 · 진행 */
export interface StarLine {
  readonly key: string;
  readonly params: Readonly<Record<string, string | number>>;
  readonly done: boolean;
  /** 0~1(없으면 막대 없음) */
  readonly fill?: number;
  /** 지금/목표 */
  readonly num?: string;
  /** 운영자 문구(있으면 key 대신 — 언어별) */
  readonly label?: { readonly ko?: string; readonly en?: string };
}

export interface SceneStars {
  readonly stars: 0 | 1 | 2 | 3;
  /** ⭐⭐ · ⭐⭐⭐ 줄(⭐ 줄은 부르는 쪽 — 장면 이름) */
  readonly lines: readonly [StarLine, StarLine];
}

type Count = (x: StarsInput) => number;
/** 횟수 장면 — [⭐, ⭐⭐, ⭐⭐⭐] 기준 · max = 장면 상태가 세는 끝(운영자 기준 상한) */
export const COUNT_STARS: Partial<
  Record<SceneId, { get: Count; at: readonly [number, number, number]; max: number }>
> = {
  stars: { get: (x) => x.play.stars, at: [3, 6, 10], max: 1000 },
  greet: { get: (x) => x.lessons.greet.friends, at: [3, 5, 8], max: FRIENDS_MAX },
  story: { get: (x) => x.lessons.story.pages.length, at: [3, 5, 8], max: PAGES_MAX },
  harbor: { get: (x) => x.lessons.harbor.ships, at: [3, 6, 10], max: SHIPS_MAX },
  boardGame: {
    get: (x) => Math.floor((x.lessons.dice.moved ?? x.lessons.dice.pos) / BOARD_LEN),
    at: [1, 2, 3],
    max: 100,
  },
  movie: { get: (x) => x.movie.loop, at: [3, 5, 8], max: 24 },
  marquee: { get: (x) => repeatsOf(x.lessons.scroll).n, at: [2, 4, 6], max: 50 },
  heartbeat: { get: (x) => x.heart.beats, at: [3, 6, 10], max: 1000 },
  goals: { get: (x) => x.lessons.goals.matched ?? 0, at: [5, 10, 15], max: GOALS_MAX },
  rps: { get: (x) => x.lessons.rps.rounds.length, at: [3, 6, 10], max: 1000 },
  reaction: {
    get: (x) => x.lessons.reaction.fastN ?? fastStarts(x.lessons.reaction),
    at: [3, 6, 10],
    max: 1000,
  },
  dice: {
    get: (x) => x.lessons.dice.count ?? x.lessons.dice.rolls.length,
    at: [6, 12, 20],
    max: 1000,
  },
  track: { get: (x) => x.track.steps, at: [50, 80, 120], max: TRACK_MAX },
  level: { get: (x) => x.lessons.level.bricks, at: [3, 6, 10], max: BRICKS_MAX },
  cheer: { get: (x) => x.lessons.stages.cheer.count ?? 0, at: [2, 3, 5], max: 1000 },
  showcase: {
    get: (x) => x.lessons.stages.showcase.visitors,
    at: [3, 6, 9],
    max: SHOWCASE_VISITORS_MAX,
  },
  melody: { get: (x) => x.lessons.stages.melody.plays ?? 0, at: [0, 1, 2], max: 20 },
  band: { get: (x) => x.lessons.stages.band.count ?? 0, at: [4, 8, 12], max: 1000 },
  plaza: { get: (x) => Math.min(x.plaza.sent, x.plaza.received), at: [3, 6, 10], max: 1000 },
};

/** 끝이 있는 장면의 시간(ms) — 기본 2분 · 1분 · 진짜 보드로만 되는 장면은 3분 · 2분 */
export const TIME_STARS: readonly [number, number] = [120_000, 60_000];
export const BOARD_ONLY_TIME: readonly [number, number] = [180_000, 120_000];
export const BOARD_ONLY: readonly SceneId[] = ["bungee", "magnetRock"];
/** 균형 잡기 — 가장 오래 버틴 시간(ms) */
export const BALANCE_STARS: readonly [number, number, number] = [5000, 10_000, 15_000];

/** 운영자가 바꾼 별 둘 · 셋(카탈로그 mb_goal payload · 2026-10-08) — 장면 종류에 맞는 칸 하나 · 문구 */
export interface MbGoalConfig {
  readonly count?: readonly [number, number];
  readonly time?: readonly [number, number];
  readonly hold?: readonly [number, number];
  readonly labels?: {
    readonly s2?: { readonly ko?: string; readonly en?: string };
    readonly s3?: { readonly ko?: string; readonly en?: string };
  };
}

export type GoalKind = "count" | "time" | "hold";
/** 장면의 별 기준 모양 — 운영 콘솔 편집기 · 검사가 쓴다 */
export interface GoalSpec {
  readonly kind: GoalKind;
  /** 별 하나 기준(횟수 · ms — 끝이 있는 장면은 null: 해내기) */
  readonly star1: number | null;
  /** 기본 [⭐⭐, ⭐⭐⭐] */
  readonly defaults: readonly [number, number];
  /** 횟수 상한(장면 상태가 여기까지 센다) */
  readonly max: number;
}

export function goalSpec(scene: SceneId): GoalSpec {
  if (scene === "balance")
    return {
      kind: "hold",
      star1: BALANCE_STARS[0],
      defaults: [BALANCE_STARS[1], BALANCE_STARS[2]],
      max: BALANCE_MAX_MS,
    };
  const c = COUNT_STARS[scene];
  if (c) return { kind: "count", star1: c.at[0], defaults: [c.at[1], c.at[2]], max: c.max };
  return {
    kind: "time",
    star1: null,
    defaults: BOARD_ONLY.includes(scene) ? BOARD_ONLY_TIME : TIME_STARS,
    max: 3_600_000,
  };
}

/** 횟수 장면의 [⭐, ⭐⭐, ⭐⭐⭐] 기준(운영자 기준이 맞으면 그것) — 횟수 장면이 아니면 null */
export function starTargets(
  scene: SceneId,
  cfg?: MbGoalConfig | null,
): readonly [number, number, number] | null {
  const spec = goalSpec(scene);
  if (spec.kind !== "count") return null;
  const [a, b] = (cfg && checkGoal(scene, cfg)) ?? spec.defaults;
  return [spec.star1 ?? 0, a, b];
}

/** 운영자 기준이 장면 모양에 맞나 — 횟수 · 버티기는 ⭐ < ⭐⭐ < ⭐⭐⭐ ≤ 상한 · 시간은 ⭐⭐ > ⭐⭐⭐ > 0(더 빨리) */
export function checkGoal(scene: SceneId, cfg: MbGoalConfig): readonly [number, number] | null {
  const spec = goalSpec(scene);
  const v = cfg[spec.kind];
  if (!v || v.length !== 2 || !v.every((n) => Number.isInteger(n) && n > 0)) return null;
  const [a, b] = v;
  if (spec.kind === "time") return a > b ? [a, b] : null;
  const floor = spec.star1 ?? 0;
  return a > floor && b > a && b <= spec.max ? [a, b] : null;
}

/** 카탈로그 mb_goal(활성만) → 장면 → 기준. 모양이 틀린 것은 뺀다(그 장면은 기본 기준표) */
export function readMbGoals(v: unknown): Map<string, MbGoalConfig> {
  const o = v as { items?: unknown } | null;
  const list: unknown[] = Array.isArray(v) ? v : Array.isArray(o?.items) ? o.items : [];
  const out = new Map<string, MbGoalConfig>();
  for (const raw of list) {
    const r = (raw ?? {}) as Record<string, unknown>;
    if (r.type != null && r.type !== "mb_goal") continue;
    if (r.enabled === false || typeof r.key !== "string") continue;
    const p = r.payload;
    if (p && typeof p === "object" && !Array.isArray(p)) out.set(r.key, p as MbGoalConfig);
  }
  return out;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const sec = (ms: number) =>
  ms / 1000 >= 10 ? String(Math.floor(ms / 1000)) : (ms / 1000).toFixed(1);

export function sceneStars(x: StarsInput, cfg?: MbGoalConfig | null): SceneStars {
  const spec = goalSpec(x.scene);
  const [a2, a3] = (cfg && checkGoal(x.scene, cfg)) ?? spec.defaults;
  const label = (k: "s2" | "s3") => cfg?.labels?.[k];
  let line: (target: number) => StarLine;
  if (spec.kind === "hold") {
    const best = x.lessons.remotes.balance.best;
    line = (ms) => ({
      key: "student.microbit.stars.hold",
      params: { s: ms / 1000 },
      done: x.star1 && best >= ms,
      fill: clamp01(best / ms),
      num: `${sec(Math.min(best, ms))}/${ms / 1000}`,
    });
  } else if (spec.kind === "count") {
    const n = COUNT_STARS[x.scene]!.get(x);
    line = (target) => ({
      key: `student.microbit.stars.count.${x.scene}`,
      params: { n: target },
      done: x.star1 && n >= target,
      fill: clamp01(n / target),
      num: `${Math.min(n, target)}/${target}`,
    });
  } else {
    line = (ms) => {
      const whole = ms % 60_000 === 0;
      return {
        key: whole ? "student.microbit.stars.within" : "student.microbit.stars.withinSec",
        params: (whole ? { m: ms / 60_000 } : { s: Math.round(ms / 1000) }) as Record<
          string,
          number
        >,
        done: x.star1 && x.doneMs !== null && x.doneMs <= ms,
      };
    };
  }
  // 운영자 문구가 있으면 붙인다(언어별 — 화면이 고른다)
  const withLabel = (l: StarLine, k: "s2" | "s3"): StarLine => {
    const t = label(k);
    return t ? { ...l, label: t } : l;
  };
  const s2 = withLabel(line(a2), "s2");
  const s3 = withLabel(line(a3), "s3");
  const stars = !x.star1 ? 0 : s3.done && s2.done ? 3 : s2.done ? 2 : 1;
  return { stars, lines: [s2, s3] };
}
